// lib
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { RouteComponentProps, useHistory, useParams } from 'react-router'

// components
import MetaTags from 'src/components/meta_tags/meta_tags'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'

// constants & interfaces
import {
  DETAILS_PAGE_TITLE,
  INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION,
  INFRASTRUCTURE_PROJECTSFEED_TITLE,
  PROJECTSFEED_DESCRIPTION,
  PROJECTSFEED_TITLE
} from 'src/constants/meta_tags'

// helpers
import { ProjectType, useProject } from '@lopo-tech/lopo-api'
import ProjectMap from 'src/components/map/project_map/project_map'
import { ProjectNotFound } from 'src/components/projects/not_found/project_not_found'
import { ProjectsCreateModal } from 'src/components/projects/project_create/projects_create_modal'
import {
  FEED_DEFAULT_PER_PAGE,
  INFRASTRUCTURE_PROJECTS,
  PUBLIC_PARTICIPATION
} from 'src/constants/feed'
import { DETAIL_IMAGE_HEIGHT, DETAIL_IMAGE_WIDTH } from 'src/constants/image'
import {
  getProjectRoutes
} from 'src/constants/urls'
import { ILocationShape } from 'src/interfaces/location'
import { textResources } from 'src/lang/de'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import HeaderProject from '../components/header/subheaders/pages/project_detail/header_project'
import ProjectDetail from '../components/projects/project_detail/project_detail'
import LayoutBase from '../layout/base/layout_base'
import LayoutNoMap from '../layout/layout_no_map'

const t = textResources.projects

interface IProps extends RouteComponentProps {
  projectType: ProjectType;
}

export const ProjectDetailPage: React.FC<IProps> = ({ projectType }) => {
  const routes = getProjectRoutes(projectType)
  const { slug } = useParams<{ slug: string }>()
  const history = useHistory()
  const [today, setToday] = useState(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  })
  const { data, loading, error, fetchMore } = useProject({
    variables: {
      slug,
      meetingFilters: { startTimeGteq: today.toISOString() },
      height: DETAIL_IMAGE_HEIGHT,
      width: DETAIL_IMAGE_WIDTH,
      page: 1,
      perPage: FEED_DEFAULT_PER_PAGE
    }
  })

  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const getNextPage = useCallback(() => {
    if (
      data?.project?.topics?.[0]?.contentItems?.pageInfo?.nextPage &&
      !loadingRef.current
    ) {
      fetchMore({
        variables: {
          page: data.project.topics[0].contentItems.pageInfo.nextPage
        }
      })
    }
  }, [fetchMore, data?.project?.topics?.[0]?.contentItems?.pageInfo?.nextPage])
  const locationShape: ILocationShape | undefined = useGetAppLocationShape()
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false)

  useEffect(() => {
    // check if the day has changed since the last render
    const date = new Date()
    date.setHours(0, 0, 0, 0)

    if (date.getTime() !== today.getTime()) {
      setToday(date)
    }
  })

  useLayoutEffect(() => {
    // A route change keeps the scroll position, and a project already in the cache
    // renders without the loading gap that collapsed the page back to the top.
    // Going back or forward keeps whatever position the browser restores, so only a
    // step into a project starts at the top. Before the paint, so the first frame of
    // the new project is not the old offset.
    if (history.action === 'POP') return
    window.scrollTo(0, 0)
  }, [slug, history])

  if (loading && !data) {
    return null
  }

  const fallbackRoute = locationShape
    ? routes.overview(locationShape.slug)
    : '/'

  const name = data?.project?.name || t.details.nameFallback
  const project = data?.project
  const topics = project?.topics || []
  const isNotFound = !!error || !project

  const isInfrastructureProject = projectType === ProjectType.InfrastructureProject
  const titleResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_TITLE : PROJECTSFEED_TITLE
  const descriptionResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION : PROJECTSFEED_DESCRIPTION

  const feedTitle = locationShape
    ? titleResolver(locationShape.name)
    : undefined
  const feedDescription = locationShape
    ? descriptionResolver(locationShape.name)
    : undefined

  const title = isNotFound ? feedTitle : DETAILS_PAGE_TITLE(name)
  const description =
    !isNotFound && project?.teaser ? project.teaser : feedDescription

  const subHeader = (
    <HeaderProject
      title={name}
      fallbackRoute={fallbackRoute}
      showMoreButton={project?.canUpdate.value}
      onEdit={() => setIsEditProjectOpen(true)}
    />
  )

  const content = (
    <>
      {isNotFound
        ? <ProjectNotFound />
        : <ProjectDetail project={project} topics={topics} getNextPage={getNextPage}
          loading={loading} />}

      {!isNotFound && isEditProjectOpen && (
        <ProjectsCreateModal
          prefill={project}
          onCancel={() => setIsEditProjectOpen(false)}
          onNext={async (projectSlug?: string) => {
            setIsEditProjectOpen(false)

            if (projectSlug && projectSlug !== slug && locationShape) {
              history.push(`${routes.overview(locationShape.slug)}/${projectSlug}`)
            }
          }}
        />
      )}
    </>
  )

  const hasGeojson = !isNotFound && !!project?.geojson
  const activeNavigationItem =
    isInfrastructureProject ? INFRASTRUCTURE_PROJECTS : PUBLIC_PARTICIPATION

  const projectMapElement = hasGeojson
    ? (
      <ProjectMap
        geojson={project!.geojson}
        boundingBox={project!.boundingBox ?? undefined}
        center={project!.boundingBox?.center ?? undefined}
      />
    )
    : undefined

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        // og_image={}
        og_url={document.location.toString()}
        title={title}
      />
      {hasGeojson
        ? (
          <LayoutBase
            subHeader={subHeader}
            pageTitle={
              <StandardPageTitle
                text={
                  isInfrastructureProject
                    ? t.common.infrastructureProjects
                    : t.common.projects
                }
                showLocation
              />
            }
            activeNavigationItem={activeNavigationItem}
            enableMapToggler={false}
            sideBarContent={projectMapElement}
            sideBarOnlyOnDesktop
          >
            {content}
          </LayoutBase>
        )
        : (
          <LayoutNoMap
            subHeader={subHeader}
            pageTitle={
              <StandardPageTitle
                text={
                  isInfrastructureProject
                    ? t.common.infrastructureProjects
                    : t.common.projects
                }
                showLocation
              />
            }
            activeNavigationItem={activeNavigationItem}
          >
            {content}
          </LayoutNoMap>
        )}
    </>
  )
}

const PublicParticipationProjectDetailPage: React.FC<RouteComponentProps> = (
  props
) => <ProjectDetailPage {...props} projectType={ProjectType.PublicParticipationProject} />

export default PublicParticipationProjectDetailPage
